import React, { ForwardRefExoticComponent, Fragment } from 'react'
import {
    Dialog,
    DialogBackdrop,
    Transition,
    TransitionChild,
} from '@headlessui/react'
import { CloseIcon } from '../icons/CloseIcon'
import { lit } from '../../utils'
import invariant from 'tiny-invariant'

const defaultHeaderMaxHeightClass = 'max-h-[4rem]'
const defaultFooterMaxHeightClass = 'max-h-[5rem]'

// This is the custom footer max-height used for the storybook demo:
// max-h-[7.5rem]
//
// **Do not delete this comment** unless we fix it so the tailwind compiler includes .mdx files
// when looking for classnames, otherwise the `max-h-[6rem]` class won't be compiled.

type DesktopModeBreakpointOption = 'sm' | 'md' | 'lg'

export interface ModalNoirProps {
    /**
     * The className -- this is mostly used to hook GA event tracking
     */
    className?: string

    /**
     * Specifies a class name on the div wrapping the modal. Currently used for syndicated components.
     */
    wrapperClassName?: string

    /**
     * Class name for the wrapper div immediately inside of DialogWrappper, which also wraps the modal contents.
     * This is used for dark mode in the global header syndicated component, which needs CSS
     * classes on two nested elements to work.
     */
    innerWrapperClassName?: string

    /**
     * Boolean to determine whether the modal is visible(true) or hidden(false)
     */
    show: boolean

    leftToRight?: boolean

    /**
     * Prop that controls the width of the modal, values in the sizeMap object below
     */
    size?: 'sm' | 'lg' | '4xl' | '6xl'

    /**
     * Callback function to handle the modal closing
     */
    onClose: () => void

    /**
     * Boolean that controls whether or not height will be full screen or based on content
     */
    fullHeightOnPhone?: boolean

    children: NonNullable<React.ReactNode>
    modalContentContainer?: React.ComponentType

    // at what breakpoint (and up) should the Modal have the desktop-style positioning and styling?
    desktopModeBreakpoint?: DesktopModeBreakpointOption
    title?: string
    showCloseButton?: boolean
    headerContent?: React.ReactNode
    bodyContent?: React.ReactNode
    footerContent?: React.ReactNode
}

type ModalComponent = ForwardRefExoticComponent<ModalNoirProps> & {
    Header: typeof Header
    Body: typeof Body
    Footer: typeof Footer
}

const verticalCenteringClassNameMap = {
    sm: 'sm:inset-0',
    md: 'md:inset-0',
    lg: 'lg:inset-0',
}

const animationCenteringClassNameMap = {
    sm: {
        enterFrom: 'sm:translate-y-0 sm:scale-95',
        enterTo: 'sm:scale-100',
        leave: 'sm:duration-700',
        leaveFrom: 'sm:scale-100',
        leaveTo: 'sm:translate-y-0 sm:scale-95',
    },
    md: {
        enterFrom: 'md:translate-y-0 md:scale-95',
        enterTo: 'md:scale-100',
        leave: 'md:duration-700',
        leaveFrom: 'md:scale-100',
        leaveTo: 'md:translate-y-0 md:scale-95',
    },
    lg: {
        enterFrom: 'lg:translate-y-0 lg:scale-95',
        enterTo: 'lg:scale-100',
        leave: 'lg:duration-700',
        leaveFrom: 'lg:scale-100',
        leaveTo: 'lg:translate-y-0 lg:scale-95',
    },
}

/**
 * Modal component
 * Implementation is based on the modal component from https://tailwindui.com/
 */
export const Modal = React.forwardRef<HTMLDivElement, ModalNoirProps>(
    (props, ref) => {
        const {
            className = 'js_Modal',
            wrapperClassName,
            show,
            leftToRight = false,
            onClose,
            children,
            modalContentContainer,
            innerWrapperClassName,
            size = 'sm',
            fullHeightOnPhone,
            desktopModeBreakpoint = 'md',
        } = props

        let header: React.ReactElement | undefined = undefined
        let body: React.ReactElement | undefined = undefined
        let footer: React.ReactElement | undefined = undefined

        React.Children.forEach(children, (child) => {
            switch ((child as any)?.type.displayName) {
                case 'Header':
                    header = React.cloneElement(
                        child as React.ReactElement<HeaderPropsIncludingInternal>,
                        {
                            _onClose: onClose,
                        }
                    )
                    break
                case 'Body':
                    body = child as React.ReactElement<ModalBodyProps>
                    break
                case 'Footer':
                    footer = child as React.ReactElement<ModalFooterProps>
                    break
                default:
                    if (child === null) {
                        return
                    }

                    if (process.env.NODE_ENV === 'development') {
                        invariant(
                            false,
                            'Modal: unexpected child elements found. ' +
                                'All body elements should be wrapped in <Modal.Body>'
                        )
                    } else {
                        // If in production, log a short warning instead of throwing an error
                        console.warn('Invariant failed')
                    }
            }
        })

        const headerMaxHeightClass =
            (header as React.ReactElement<ModalHeaderProps> | undefined)?.props
                .maxHeightClass || defaultHeaderMaxHeightClass
        const footerMaxHeightClass = (footer as
            | React.ReactElement<ModalFooterProps>
            | undefined)
            ? (footer as unknown as React.ReactElement<any>).props
                  .maxHeightClass || defaultFooterMaxHeightClass
            : // footer is optional, so default to zero when there's no footer
              'max-h-0'
        const headerMaxHeight = getMaxHeightValue(headerMaxHeightClass)
        const footerMaxHeight = getMaxHeightValue(footerMaxHeightClass)

        if (!body) {
            if (process.env.NODE_ENV === 'development') {
                throw Error('Modal.Body is required')
            }
            return null
        }

        body = React.cloneElement(body, {
            _headerMaxHeight: headerMaxHeight,
            _footerMaxHeight: footerMaxHeight,
        })

        const ModalContentContainer = modalContentContainer ?? Fragment

        const sizeMap = {
            // Todo: Should we increase the size of sm so that it is always full width for most phones?
            sm: 'w-[24rem]', // w-[384px]
            lg: 'w-[32rem]', // w-[512px]
            '4xl': 'w-[56rem]', // w-[896px]
            '6xl': 'w-[72rem] h-screen-svh', // w-[1152px]
        }

        const verticalCenteringClassNames =
            verticalCenteringClassNameMap[desktopModeBreakpoint]
        const animationCenteringClassNames =
            animationCenteringClassNameMap[desktopModeBreakpoint]

        return (
            <Transition show={show} as={Fragment} appear={true}>
                <Dialog
                    className={lit`fixed bottom-0 left-0 right-0 z-[1500] overflow-y-hidden p-0 ${verticalCenteringClassNames} sm:flex sm:items-center sm:justify-center`}
                    ref={ref}
                    onClose={onClose}
                    as={DialogWrapper}
                    wrapperClassName={wrapperClassName}
                >
                    <div
                        className={`flex h-full items-center justify-center text-center ${innerWrapperClassName}`}
                    >
                        <TransitionChild
                            as={Fragment}
                            enter={
                                leftToRight
                                    ? 'transform transition ease-in-out duration-500 sm:duration-700'
                                    : 'ease-out duration-300'
                            }
                            enterFrom={
                                leftToRight ? '-translate-x-full' : 'opacity-0'
                            }
                            enterTo={
                                leftToRight ? 'translate-x-0' : 'opacity-100'
                            }
                            leave={
                                leftToRight
                                    ? 'transform transition ease-in-out duration-500 sm:duration-700'
                                    : 'ease-in duration-200'
                            }
                            leaveFrom={
                                leftToRight ? 'translate-x-0' : 'opacity-100'
                            }
                            leaveTo={
                                leftToRight ? '-translate-x-full' : 'opacity-0'
                            }
                        >
                            {/* this is the backdrop */}
                            <DialogBackdrop
                                className={lit`fixed inset-0 bg-black/[.64] transition-opacity`}
                            />
                        </TransitionChild>

                        <TransitionChild
                            as={Fragment}
                            enter={
                                leftToRight
                                    ? 'transform transition ease-in-out duration-500'
                                    : 'ease-in duration-500'
                            }
                            enterFrom={
                                leftToRight
                                    ? '-translate-x-full'
                                    : `opacity-0 translate-y-3/4 ${animationCenteringClassNames.enterFrom}`
                            }
                            enterTo={
                                leftToRight
                                    ? 'translate-x-0'
                                    : `opacity-100 translate-y-0 ${animationCenteringClassNames.enterTo}`
                            }
                            leave={
                                leftToRight
                                    ? `transform transition ease-in-out duration-500 ${animationCenteringClassNames.leave}`
                                    : 'ease-in duration-500'
                            }
                            leaveFrom={
                                leftToRight
                                    ? 'translate-x-0'
                                    : `opacity-100 translate-y-0 ${animationCenteringClassNames.leaveFrom}`
                            }
                            leaveTo={
                                leftToRight
                                    ? '-translate-x-full'
                                    : `opacity-0 translate-y-3/4 ${animationCenteringClassNames.leaveTo}`
                            }
                        >
                            {/* actual modal starts here */}
                            <div
                                className={lit`${
                                    fullHeightOnPhone && 'h-screen-svh'
                                } ${sizeMap[size]}
                        ${size !== '6xl' && 'sm:!h-auto portrait:sm:!h-auto'} 
                        
                        inline-block max-w-[100vw] transform overflow-y-auto bg-white px-4 align-bottom
                        transition-all sm:align-middle
                        ${className}`}
                            >
                                <ModalContentContainer>
                                    <div
                                        className={lit`max-h-screen-svh flex h-full flex-col`}
                                    >
                                        {header ?? (
                                            <Header _onClose={onClose} />
                                        )}
                                        {body}
                                        {footer}
                                    </div>
                                </ModalContentContainer>
                            </div>
                        </TransitionChild>
                    </div>
                </Dialog>
            </Transition>
        )
    }
) as ModalComponent

Modal.displayName = 'Modal'

export interface ModalHeaderProps {
    children?: React.ReactNode
    title?: string
    maxHeightClass?: string
    className?: string
    showCloseButton?: boolean
    alignChildrenWithCloseIcon?: boolean
}

type HeaderPropsIncludingInternal = ModalHeaderProps & {
    // internal prop
    _onClose?: () => void
}

function Header({
    children,
    title,
    maxHeightClass,
    className,
    showCloseButton = true,
    alignChildrenWithCloseIcon = false,
    _onClose, // we will be passing this directly from the parent Modal component (via React.cloneElement)
}: HeaderPropsIncludingInternal) {
    const isClosingRef = React.useRef(false)

    function handleClose() {
        if (_onClose && !isClosingRef.current) {
            _onClose()
        }
        isClosingRef.current = true
    }

    // set the close button to the default height (32px)
    // so that the focus indicator isn't the height of the entire header with children
    const closeIconHeightClass = alignChildrenWithCloseIcon ? 'h-8' : ''

    return (
        <header
            className={lit`py-4 ${className} ${
                maxHeightClass || defaultHeaderMaxHeightClass
            }`}
        >
            <div className="flex w-full flex-row justify-between">
                {title ? (
                    <h1 className="flex items-center font-bold">{title}</h1>
                ) : alignChildrenWithCloseIcon ? (
                    <>{children}</>
                ) : (
                    <div />
                )}
                {showCloseButton && (
                    <div className={lit`flex ${closeIconHeightClass}`}>
                        <button
                            onClick={handleClose}
                            title="Close"
                            type="button"
                        >
                            <CloseIcon size="large" />
                        </button>
                    </div>
                )}
            </div>
            {!alignChildrenWithCloseIcon && <>{children}</>}
        </header>
    )
}
Header.displayName = 'Header'
Modal.Header = Header

export interface ModalBodyProps {
    children: NonNullable<React.ReactNode>
    className?: string
}

function Body({
    children,
    className,
    _headerMaxHeight = 'max-h-0',
    _footerMaxHeight = 'max-h-0',
}: ModalBodyProps & {
    // internal props
    _headerMaxHeight?: string
    _footerMaxHeight?: string
}) {
    // use svh units if the browser supports them to avoid issues on iPhone.
    // see https://stackoverflow.com/a/75648985/560114
    const vhUnits =
        typeof document !== 'undefined' &&
        CSS.supports && // Optional chaining (?.) did not work here for some reason
        CSS.supports('height: 1svh')
            ? 'svh'
            : 'vh'
    return (
        <div
            className={lit`flex grow flex-col overflow-y-auto pb-4 ${className}`}
            style={{
                maxHeight: `calc(100${vhUnits} - ${_headerMaxHeight} - ${_footerMaxHeight})`,
            }}
        >
            {children}
        </div>
    )
}
Body.displayName = 'Body'
Modal.Body = Body

export interface ModalFooterProps {
    children?: React.ReactNode
    maxHeightClass?: string
    className?: string
}

function Footer({ children, maxHeightClass, className }: ModalFooterProps) {
    return (
        <footer
            className={lit`pb-4 sm:pb-6 ${className} ${
                maxHeightClass || defaultFooterMaxHeightClass
            }`}
        >
            {children}
        </footer>
    )
}
Footer.displayName = 'Footer'
Modal.Footer = Footer

// NOTE: this currently only supports custom Tailwind size, e.g. max-h-[10rem]
function getMaxHeightValue(tailwindClassName: string) {
    let isValid = tailwindClassName.startsWith('max-h-')

    // Account for custom Tailwind size, e.g. max-h-[10rem]
    if (isValid) {
        const heightString = tailwindClassName.split(/\[|\]/)[1]
        // Check for a number followed by rem/px/vh unit.
        // Note: percentage heights are deliberately not allowed here, since they
        // don't work with our overflow/scrolling implementation.
        const regex = /^\d*(\.\d+)?(rem|px|vh)$/
        if (tailwindClassName === 'max-h-0') {
            return '0px' // note: calc() requires `0px` (or some units) rather than just `0`
        } else if (heightString && regex.test(heightString)) {
            return heightString
        } else {
            isValid = false
        }
    }

    if (!isValid) {
        if (process.env.NODE_ENV === 'development') {
            throw Error(
                `Error: Height must be a tailwind class using a number followed by 'rem', 'px', or 'vh', e.g. 'max-h-[10rem]. ` +
                    // TODO update error message if/when we support standard sizes
                    'Standard tailwind sizes are not yet supported. ' +
                    `Got '${tailwindClassName}' instead.`
            )
        }
        return false
    }
}

interface DialogWrapperProps {
    className?: string
    children?: React.ReactNode
    wrapperClassName?: string
}

const DialogWrapper = React.forwardRef<HTMLDivElement, DialogWrapperProps>(
    ({ wrapperClassName, className, children }, ref) => (
        <div className={wrapperClassName}>
            <div ref={ref} className={className}>
                {children}
            </div>
        </div>
    )
)
DialogWrapper.displayName = 'DialogWrapper'
